import BaseWorldInitializer from './modules/BaseWorld/BaseWorldInitializer.js';
import BaseWorldTransition from './modules/BaseWorld/BaseWorldTransition.js';
import BaseWorldMultiplayer from './modules/BaseWorld/BaseWorldMultiplayer.js';
import MobileControls from './modules/MobileControls/MobileControls.js';
import DebugHelper from './modules/DebugHelper.js';
import ItemDataLoader from './modules/ItemDataLoader.js';
import ErrorHandler from './modules/ErrorHandler.js';
import { deepClone } from './modules/utils/PlayerDataSync.js';
import { isRegisteredWorldScene } from './modules/SceneRegistry.js';
import GameStateStore from './modules/GameStateStore.js';
import { ensureMapTextures } from './modules/LazyAssetLoader.js';
import AutoSaveManager from './modules/AutoSaveManager.js';
import AutoSaveToast from './modules/AutoSaveToast.js';
import MapLoadingOverlay from './modules/MapLoadingOverlay.js';
import { publishCombatState } from './modules/CombatStateSync.js';
import WorldChunkManager from './modules/WorldChunkManager.js';
import WorldMapOverlay from './modules/WorldMapOverlay.js';
import { isCave } from './modules/SceneClassHelper.js';

export default class BaseWorldScene extends Phaser.Scene {
    constructor(sceneKey, mapKey, config = {}) {
        super(sceneKey);
        
        this.mapKey = mapKey;
        this.config = {
            enableRandomEncounter: config.enableRandomEncounter !== false,
            enableResumeEvent: config.enableResumeEvent !== false,
            welcomeMessage: config.welcomeMessage || '',
            bgmKey: config.bgmKey || null,
            ...config
        };
        
        this.worldMap = null;
        this.worldNPC = null;
        this.worldPlayer = null;
        this.worldUI = null;
        this.worldInteraction = null;
        
        this.gameState = null;
        this.player = null;
        this.playerSprite = null;
        this.vehicleSprite = null;
        this.inVehicle = false;
        this.movementEnabled = true;
        this.isTransitioning = false;
        
        this.interactiveObjects = [];
        this.transitionPoints = [];
        
        this.cursors = null;
        this.spaceKey = null;
        this.vKey = null;
        this.eKey = null;
        
        this.teamManager = null;
        this.bgm = null;
        this.mobileControls = null;
        
        this.initializer = new BaseWorldInitializer(this);
        this.transition = new BaseWorldTransition(this);
        this.multiplayer = new BaseWorldMultiplayer(this);
        this.mapLoadingOverlay = null;
        this._battleStartUnsubscribe = null;
        this.chunkManager = null;
        this.isChunkedWorld = false;
        this.worldMapOverlay = null;
        this.mKey = null;
        this.escKey = null;
    }
    
    /**
     * 初始化场景
     * @param {Object} data - 初始化数据（可选）
     */
    init(data = {}) {
        const initResult = this.initializer.init(data);
        if (!initResult) return;
        
        this.player = initResult.player;
        this.inVehicle = initResult.inVehicle;
        this.isMultiplayer = initResult.isMultiplayer;
        this.networkManager = initResult.networkManager;
        this.needsTransitionIn = initResult.needsTransitionIn;
        this.isTransitioning = false;

        if (!this._wakeHookRegistered) {
            this.events.on('wake', (_scene, data) => this.handleSceneWake(data));
            this._wakeHookRegistered = true;
        }
    }

    handleSceneWake(data = {}) {
        this.isTransitioning = false;
        this.movementEnabled = true;
        AutoSaveToast.bindScene(this);
        GameStateStore.setCurrentScene(this.scene.key);

        if (data.spawnX !== undefined && data.spawnY !== undefined) {
            this.initializer.applySpawnPosition(data);
            if (this.isChunkedWorld && this.chunkManager && this.playerSprite) {
                this.chunkManager.refreshAroundSprite(this.playerSprite).catch((error) => {
                    DebugHelper.warn('BaseWorldScene: 传送后区块刷新失败', error);
                });
            }
        }

        if (this.isMultiplayer && this.networkManager && this.player) {
            publishCombatState(this.networkManager, this.player);
        }

        if (this.cameras?.main) {
            this.cameras.main.fadeIn(300);
        }
    }
    
    initializeItemDataLoader() {
        if (this.cache.json.exists('items')) {
            if (!ItemDataLoader.isInitialized()) {
                ItemDataLoader.initialize(this);
            }
        } else {
            DebugHelper.warn('BaseWorldScene: items.json 未加载，商品数据功能可能不可用');
        }
    }
    
    create() {
        this.initializeItemDataLoader();

        if (!this.worldMapJson) {
            ErrorHandler.handleError('地图数据未就绪', '世界场景创建', this);
            return;
        }

        this.mapLoadingOverlay = new MapLoadingOverlay(this);
        this.mapLoadingOverlay.show('正在准备地图...');

        if (this.isChunkedWorld) {
            this.chunkManager = new WorldChunkManager(this);
            this.initializer.initializeModules();
            this.chunkManager.initialize()
                .then(() => {
                    this.mapLoadingOverlay.hide();
                    this.finishBuildWorld();
                })
                .catch((error) => {
                    this.mapLoadingOverlay.hide();
                    ErrorHandler.handleError(error, '大地图区块加载', this);
                });
            return;
        }

        ensureMapTextures(this, this.worldMapJson)
            .then(() => {
                this.mapLoadingOverlay.hide();
                this.buildWorld();
            })
            .catch((error) => {
                this.mapLoadingOverlay.hide();
                ErrorHandler.handleError(error, '地图资源加载', this);
            });
    }

    buildWorld() {
        this.initializer.initializeModules();
        this.finishBuildWorld();
    }

    finishBuildWorld() {
        this.initializer.createWorldMap();
        this.initializer.createNPCs();
        this.initializer.createEnemies();
        this.initializer.createPlayer();
        this.initializer.setupCollisions();
        this.initializer.createUI();
        this.initializer.setupInteractions();
        
        this.mobileControls = new MobileControls(this);
        this.mobileControls.initialize();
        
        this.initializer.setupCamera();
        this.initializer.playBGM().catch((error) => {
            DebugHelper.warn('BaseWorldScene: BGM 播放失败', error);
        });
        this.initializer.showWelcomeMessage();
        
        // 多人游戏模式下，通知服务器当前场景ID
        if (this.isMultiplayer && this.networkManager) {
            this.time.delayedCall(500, () => {
                this.networkManager.updatePlayerState({
                    sceneId: this.scene.key
                });
                DebugHelper.log('已通知服务器当前场景:', this.scene.key);
            });
        }
        
        if (this.config.enableResumeEvent) {
            this.setupResumeEvent();
        }
        
        if (this.isMultiplayer && this.networkManager) {
            // 在初始化多人游戏之前，先同步场景中的玩家数据到gameState
            // 这样可以确保连接服务器时使用的是正确的玩家数据
            if (this.player && GameStateStore.getPlayer()) {
                GameStateStore.syncFromScene(this.player, {
                    includeVehicle: true,
                    includePosition: true,
                    position: { x: this.player.x, y: this.player.y },
                    tilePosition: { tileX: this.player.tileX, tileY: this.player.tileY },
                    inVehicle: this.player.inVehicle
                });
            }
            
            this.multiplayer.initializeMultiplayer(this.networkManager);
            
            if (this._battleStartUnsubscribe) {
                this._battleStartUnsubscribe();
            }
            this._battleStartUnsubscribe = this.networkManager.on('battleStart', (battleData) => {
                this.handleTeamBattleStart(battleData);
            });

            publishCombatState(this.networkManager, this.player);
        }
        
        AutoSaveToast.bindScene(this);
        
        // 检查是否是副本场景，如果是人类状态，立即传送到出口
        this.checkAndTeleportFromCave();
        
        this.transition.setupExits();
        this.setupWorldMapControls();
        this.onSceneCreated();
        this.initializer.performTransitionIn();

        GameStateStore.setCurrentScene(this.scene.key);
        AutoSaveManager.startPeriodicSave();
    }
    
    setupResumeEvent() {
        this.events.on('resume', (scene, data) => {
            DebugHelper.log('场景恢复事件触发', data);
            
            const battleScene = this.scene.get('BattleScene');
            if (battleScene && battleScene.scene.isActive()) {
                return;
            }

            // 优先使用场景中的玩家数据，如果gameState.player无效（属性都是0），则使用this.player
            let latestPlayerData = null;
            const gameStatePlayer = GameStateStore.getPlayer();
            if (gameStatePlayer) {
                // 检查gameState.player是否有效（至少有一个非0的属性）
                const hasValidData = gameStatePlayer.hp > 0 || gameStatePlayer.maxHp > 0 || 
                                    gameStatePlayer.attack > 0 || gameStatePlayer.defense > 0 ||
                                    gameStatePlayer.money > 0 || gameStatePlayer.exp > 0 ||
                                    (gameStatePlayer.level && gameStatePlayer.level > 0);
                
                if (hasValidData) {
                    latestPlayerData = gameStatePlayer;
                } else if (this.player) {
                    // gameState.player无效，使用场景中的player数据
                    DebugHelper.warn('gameState.player数据无效，使用场景中的player数据');
                    latestPlayerData = this.player;
                } else if (data && data.player) {
                    latestPlayerData = data.player;
                }
            } else if (data && data.player) {
                latestPlayerData = data.player;
            } else if (this.player) {
                latestPlayerData = this.player;
            }
            
            if (latestPlayerData && this.worldUI) {
                try {
                    this.worldUI.updateHUD(latestPlayerData);
                } catch (error) {
                    DebugHelper.warn('更新HUD失败（场景可能已暂停）:', error);
                }
                
                if (this.player) {
                    // 优先使用当前玩家精灵的位置（这是玩家在暂停场景时的实际位置）
                    // 这样可以确保菜单关闭后玩家不会回到出生点
                    let originalX, originalY;
                    
                    // 如果玩家精灵存在且活跃，使用精灵的当前位置（这是最准确的）
                    if (this.playerSprite && this.playerSprite.active) {
                        originalX = this.playerSprite.x;
                        originalY = this.playerSprite.y;
                    } else if (this.vehicleSprite && this.vehicleSprite.active) {
                        // 如果只有战车精灵存在，使用战车的位置
                        originalX = this.vehicleSprite.x;
                        originalY = this.vehicleSprite.y;
                    } else if (this.player.x !== undefined && this.player.y !== undefined) {
                        // 使用this.player的位置
                        originalX = this.player.x;
                        originalY = this.player.y;
                    } else if (gameStatePlayer &&
                              gameStatePlayer.x !== undefined && gameStatePlayer.y !== undefined) {
                        // 从gameState读取位置
                        originalX = gameStatePlayer.x;
                        originalY = gameStatePlayer.y;
                    } else {
                        // 最后回退到latestPlayerData的位置
                        originalX = latestPlayerData.x || 0;
                        originalY = latestPlayerData.y || 0;
                    }
                    
                    const originalInVehicle = this.player.inVehicle !== undefined ? this.player.inVehicle : 
                                             (gameStatePlayer ? gameStatePlayer.inVehicle : false);
                    const originalVehicle = this.player.vehicle || 
                                          (gameStatePlayer ? gameStatePlayer.vehicle : null);
                    
                    // 更新玩家属性（但不改变位置），只更新有效的属性
                    if (latestPlayerData.hp !== undefined && latestPlayerData.hp !== null) {
                        this.player.hp = latestPlayerData.hp;
                    }
                    if (latestPlayerData.maxHp !== undefined && latestPlayerData.maxHp !== null) {
                        this.player.maxHp = latestPlayerData.maxHp;
                    }
                    if (latestPlayerData.money !== undefined && latestPlayerData.money !== null) {
                        this.player.money = latestPlayerData.money;
                    }
                    if (latestPlayerData.exp !== undefined && latestPlayerData.exp !== null) {
                        this.player.exp = latestPlayerData.exp;
                    }
                    if (latestPlayerData.level !== undefined && latestPlayerData.level !== null) {
                        this.player.level = latestPlayerData.level;
                    }
                    if (latestPlayerData.nextLevelExp !== undefined && latestPlayerData.nextLevelExp !== null) {
                        this.player.nextLevelExp = latestPlayerData.nextLevelExp;
                    }
                    if (latestPlayerData.attack !== undefined && latestPlayerData.attack !== null) {
                        this.player.attack = latestPlayerData.attack;
                    }
                    if (latestPlayerData.defense !== undefined && latestPlayerData.defense !== null) {
                        this.player.defense = latestPlayerData.defense;
                    }
                    if (latestPlayerData.inventory !== undefined && latestPlayerData.inventory !== null) {
                        this.player.inventory = latestPlayerData.inventory;
                    }
                    if (latestPlayerData.equipment !== undefined && latestPlayerData.equipment !== null) {
                        this.player.equipment = latestPlayerData.equipment;
                    }
                    if (latestPlayerData.skills !== undefined && latestPlayerData.skills !== null) {
                        this.player.skills = latestPlayerData.skills;
                    }
                    
                    // 同步战车数据（保持当前位置和车辆状态）
                    if (latestPlayerData.vehicle) {
                        this.player.vehicle = originalVehicle || deepClone(latestPlayerData.vehicle);
                    }
                    
                    // 保持当前位置（不改变）
                    this.player.x = originalX;
                    this.player.y = originalY;
                    this.player.inVehicle = originalInVehicle;
                    
                    // 同步精灵位置（实际上位置应该已经正确了，这里只是确保同步）
                    if (this.playerSprite && this.playerSprite.active) {
                        this.playerSprite.x = originalX;
                        this.playerSprite.y = originalY;
                    }
                    
                    if (this.vehicleSprite && this.vehicleSprite.active) {
                        this.vehicleSprite.x = originalX;
                        this.vehicleSprite.y = originalY;
                    }
                    
                    // 更新gameState中的位置和属性，确保数据被保存
                    if (GameStateStore.getPlayer()) {
                        GameStateStore.patchPlayer({ x: originalX, y: originalY });
                        GameStateStore.syncFromScene(this.player, {
                            includeVehicle: true,
                            includePosition: false
                        });
                    }
                }
            }

            if (data && data.from === 'BattleScene') {
                const currentTime = Date.now();
                GameStateStore.setLastEncounterTime(currentTime - 7000);
            }
            
            const shouldPlayBGM = (data && data.from === 'BattleScene') ||
                                 (data && data.from && data.from !== 'MenuScene' && data.from !== 'ShopScene' && data.from !== 'VehicleEquipmentScene') ||
                                 !this.bgm ||
                                 !this.bgm.isPlaying;
            
            if (shouldPlayBGM) {
                if (this.bgm && this.bgm.isPlaying) {
                    this.bgm.stop();
                }
                this.initializer.playBGM().catch((error) => {
            DebugHelper.warn('BaseWorldScene: BGM 播放失败', error);
        });
            }
        });
    }
    
    transitionToScene(exit, options = {}) {
        this.transition.transitionToScene(exit, options);
    }
    
    setupWorldMapControls() {
        if (!this.isChunkedWorld) {
            return;
        }

        this.worldMapOverlay = new WorldMapOverlay(this);
        this.mKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.M);
        this.escKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.ESC);
    }

    handleWorldMapOverlayInput() {
        if (!this.isChunkedWorld || !this.worldMapOverlay) {
            return false;
        }

        if (this.worldUI?.isDialogActive || this.isTransitioning) {
            return this.worldMapOverlay.isOpen;
        }

        if (this.mKey && Phaser.Input.Keyboard.JustDown(this.mKey)) {
            this.worldMapOverlay.toggle();
            return true;
        }

        if (this.worldMapOverlay.isOpen && this.escKey && Phaser.Input.Keyboard.JustDown(this.escKey)) {
            this.worldMapOverlay.hide();
            return true;
        }

        return this.worldMapOverlay.isOpen;
    }

    onSceneCreated() {
    }
    
    /**
     * 检查是否是副本场景，如果是人类状态，立即传送到出口
     */
    checkAndTeleportFromCave() {
        // 检查是否需要传送（在createPlayer中设置的标记）
        if (!this.needsCaveTeleport) {
            return;
        }
        
        // 再次检查是否在车上，如果在车上则不传送
        const isInVehicle = this.inVehicle || (this.player && this.player.inVehicle);
        if (isInVehicle) {
            DebugHelper.log('checkAndTeleportFromCave: 玩家在车上，取消传送');
            this.needsCaveTeleport = false; // 清除标记
            return;
        }
        
        if (!this.worldMapJson) {
            DebugHelper.warn('checkAndTeleportFromCave: worldMapJson 未加载');
            return;
        }
        
        const sceneConfig = this.worldMapJson?.sceneConfig;
        
        // 再次确认是副本场景
        if (!isCave(sceneConfig)) {
            DebugHelper.warn('checkAndTeleportFromCave: 不是副本场景，取消传送');
            this.needsCaveTeleport = false; // 清除标记
            return;
        }
        
        const exits = this.worldMapJson.exits || [];
        DebugHelper.log('checkAndTeleportFromCave: 检测到需要传送，exits数量:', exits.length, 'inVehicle:', isInVehicle);
        
        if (exits.length > 0) {
            // 找到第一个出口（通常是传送到世界地图的出口）
            const exit = exits[0];
            DebugHelper.log('checkAndTeleportFromCave: 找到出口，目标场景:', exit.targetScene, '目标位置:', exit.targetTileX, exit.targetTileY);
            
            // 立即执行传送，不延迟
            if (exit.targetScene && exit.targetTileX !== undefined && exit.targetTileY !== undefined) {
                DebugHelper.log('checkAndTeleportFromCave: 开始传送');
                // 使用场景转换逻辑，直接传送到出口（不使用淡入淡出，立即传送）
                if (this.transition) {
                    this.transition.transitionToScene(exit, { useFade: false, sound: 'into' });
                } else {
                    DebugHelper.error('checkAndTeleportFromCave: transition 对象不存在');
                }
            } else {
                DebugHelper.error('checkAndTeleportFromCave: 出口配置不完整', exit);
            }
        } else {
            DebugHelper.warn('checkAndTeleportFromCave: 副本中没有找到出口配置');
        }
    }
    
    update(time, delta) {
        if (!this.input || !this.input.keyboard) return;
        if (!this.cursors) return;

        if (this.handleWorldMapOverlayInput()) {
            return;
        }
        
        if (this.movementEnabled && !this.worldUI.isDialogActive) {
            const currentSprite = this.worldPlayer.getCurrentControlledSprite();
            if (!currentSprite) return;
            
            // 检查是否是组队模式，以及是否是队长
            let canMove = true;
            if (this.isMultiplayer && this.multiplayer.getTeamId()) {
                // 在队伍中，只有队长可以移动
                canMove = this.multiplayer.isTeamLeader();
            }
            
            if (this.worldPlayer && canMove && this.playerSprite) {
                const activeCursors = this.mobileControls && this.mobileControls.enabled 
                    ? this.mobileControls.getCursors() 
                    : this.cursors;
                
                if (activeCursors) {
                    this.worldPlayer.updatePlayerMovement(activeCursors, 120);
                }
                
                if (this.isMultiplayer && currentSprite) {
                    const direction = this.worldPlayer.getCurrentDirection() || 'down';
                    this.multiplayer.updateLocalPlayerPosition(
                        currentSprite.x,
                        currentSprite.y,
                        direction
                    );
                    
                    if (this.multiplayer.isTeamLeader()) {
                        this.multiplayer.updateTeamMemberPositions();
                    }
                }
            } else if (this.isMultiplayer && this.multiplayer.getTeamId() && !this.multiplayer.isTeamLeader()) {
                if (currentSprite) {
                    this.multiplayer.updateFollowerPosition(currentSprite);
                }
            }
            
            if (this.worldPlayer) {
                this.worldPlayer.updateVehicleFollowing();
            }
            
            if (this.worldMap && currentSprite) {
                this.worldMap.checkWorldBounds(currentSprite);
            }
            
            this.worldInteraction.checkMapTransitions();
            
            if (this.vKey && Phaser.Input.Keyboard.JustDown(this.vKey)) {
                this.worldPlayer.toggleVehicle(this.player, this.player.vehicle);
            }
        }

        if (this.isChunkedWorld && this.chunkManager && this.playerSprite) {
            this.chunkManager.update(time, this.playerSprite);
        }

        if (this.config.enableRandomEncounter) {
            this.worldInteraction.checkRandomEncounter();
        }

        if (this.worldNPC) {
            this.worldNPC.update();
        }

        if (this.worldUI) {
            this.worldUI.update();
            
            if (this.mobileControls && this.mobileControls.enabled) {
                const shouldShow = !this.worldUI.isDialogActive;
                this.mobileControls.setVisible(shouldShow);
            }
        }

        if (this.isMultiplayer && this.multiplayerPlayerManager) {
            this.multiplayerPlayerManager.tick(delta);
        }
        
        if (this.cameraCenterBounds) {
            const camera = this.cameras.main;
            const centerX = camera.scrollX + camera.width / 2;
            const centerY = camera.scrollY + camera.height / 2;
            
            let newCenterX = centerX;
            let newCenterY = centerY;
            
            // 限制相机中心位置
            if (centerX < this.cameraCenterBounds.minX) {
                newCenterX = this.cameraCenterBounds.minX;
            } else if (centerX > this.cameraCenterBounds.maxX) {
                newCenterX = this.cameraCenterBounds.maxX;
            }
            
            if (centerY < this.cameraCenterBounds.minY) {
                newCenterY = this.cameraCenterBounds.minY;
            } else if (centerY > this.cameraCenterBounds.maxY) {
                newCenterY = this.cameraCenterBounds.maxY;
            }
            
            // 如果位置需要调整，更新相机位置
            if (newCenterX !== centerX || newCenterY !== centerY) {
                camera.setScroll(
                    newCenterX - camera.width / 2,
                    newCenterY - camera.height / 2
                );
            }
        }
    }
    
    /**
     * 处理队伍战斗开始消息（队员接收队长触发的战斗）
     * @param {Object} battleData - 战斗数据
     */
    handleTeamBattleStart(battleData) {
        // 检查是否已经在战斗场景中
        const battleScene = this.scene.get('BattleScene');
        if (battleScene && battleScene.scene && battleScene.scene.isActive()) {
            DebugHelper.log('已经在战斗场景中，忽略战斗开始消息');
            return;
        }
        
        if (!isRegisteredWorldScene(this.scene.key)) {
            DebugHelper.warn('收到战斗开始消息，但当前不在世界场景');
            return;
        }
        
        DebugHelper.log('收到队伍战斗开始消息，启动战斗场景', battleData);
        
        // 更新队员的玩家数据（使用自己的最新数据）
        if (battleData.teamMembers) {
            const localPlayerId = this.networkManager ? this.networkManager.playerId : null;
            const localPlayer = GameStateStore.getPlayer();
            if (localPlayerId && localPlayer) {
                const teamMember = battleData.teamMembers.find(m => m.playerId === localPlayerId);
                if (teamMember) {
                    teamMember.playerData = localPlayer;
                }
            }
        }
        
        if (this.playerSprite) {
            const tileSize = 32;
            const playerX = this.playerSprite.x;
            const playerY = this.playerSprite.y;
            
            GameStateStore.patchPlayer({
                x: playerX,
                y: playerY,
                tileX: Math.round((playerX - tileSize / 2) / tileSize),
                tileY: Math.round((playerY - tileSize / 2) / tileSize)
            });
        }
        
        // 停止BGM
        if (this.bgm) {
            this.bgm.stop();
        }
        
        // 暂停当前场景并启动战斗场景
        this.scene.pause(this.scene.key);
        this.scene.launch('BattleScene', battleData);
    }
    
    shutdown() {
        if (this._battleStartUnsubscribe) {
            this._battleStartUnsubscribe();
            this._battleStartUnsubscribe = null;
        }

        if (this.teamManager?.destroy) {
            this.teamManager.destroy();
            this.teamManager = null;
        }

        if (this.multiplayerPlayerManager?.destroy) {
            this.multiplayerPlayerManager.destroy();
            this.multiplayerPlayerManager = null;
        }

        AutoSaveToast.destroy();

        if (this.worldMapOverlay) {
            this.worldMapOverlay.destroy();
            this.worldMapOverlay = null;
        }

        if (this.chunkManager) {
            this.chunkManager.destroy();
            this.chunkManager = null;
        }

        if (this.mapLoadingOverlay) {
            this.mapLoadingOverlay.hide();
            this.mapLoadingOverlay = null;
        }

        if (this.mobileControls) {
            this.mobileControls.destroy();
            this.mobileControls = null;
        }
    }
}
